import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  DatePicker,
  Dropdown,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Select,
  Spin,
} from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import InvestmentFormModal from "./components/InvestmentFormModal";
import InvestmentDetailsDrawer from "./components/InvestmentDetailsDrawer";
import {
  addManualPayment,
  createInvestment,
  fetchInvestmentDetails,
  fetchInvestments,
  markInstallmentPaid,
  deleteInvestment,
  fetchLocations,
  updateInvestment,
  updatePayment,
  deletePayment as deletePaymentApi,
} from "./clientInvestmentsService";
import {
  ClientInvestment,
  ClientInvestmentPayment,
  ClientLocation,
} from "./types";
import "./ClientInvestments.scss";

type StatusFilter = "all" | "active" | "completed";

const fmt = (n: number) => Math.round(n).toLocaleString();
const fmtK = (n: number) =>
  n >= 1e6
    ? (n / 1e6).toFixed(2) + "M"
    : n >= 1e3
    ? Math.round(n / 1e3) + "K"
    : String(Math.round(n));

const SearchIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
  >
    <circle cx="11" cy="11" r="7" />
    <path d="M21 21l-4.5-4.5" />
  </svg>
);

const KebabIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="currentColor"
  >
    <circle cx="12" cy="5" r="1.6" />
    <circle cx="12" cy="12" r="1.6" />
    <circle cx="12" cy="19" r="1.6" />
  </svg>
);

const CheckIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

const ClientInvestments: React.FC = () => {
  const { t } = useTranslation();
  const userData = useSelector((state: any) => state.userData.user);
  const storedUser = React.useMemo(() => {
    const raw = localStorage.getItem("user");
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }, []);

  const clientId =
    (typeof userData?.clientId === "object"
      ? userData?.clientId?._id
      : userData?.clientId) ||
    (typeof storedUser?.clientId === "object"
      ? storedUser?.clientId?._id
      : storedUser?.clientId);

  const token = userData?.accessToken || storedUser?.accessToken;

  const [investments, setInvestments] = useState<ClientInvestment[]>([]);
  const [locations, setLocations] = useState<ClientLocation[]>([]);
  const [loading, setLoading] = useState(false);
  const [savingInvestment, setSavingInvestment] = useState(false);
  const [investmentFormOpen, setInvestmentFormOpen] = useState(false);
  const [editingInvestment, setEditingInvestment] =
    useState<ClientInvestment | null>(null);
  const [drawerInvestment, setDrawerInvestment] =
    useState<ClientInvestment | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [paymentModal, setPaymentModal] = useState<{
    open: boolean;
    investmentId?: string;
    payment?: ClientInvestmentPayment | null;
  }>({ open: false });
  const [paymentForm] = Form.useForm<{
    amount?: number;
    paymentDate?: dayjs.Dayjs;
    note?: string;
  }>();
  const [savingPayment, setSavingPayment] = useState(false);
  const [deletingPaymentId, setDeletingPaymentId] = useState<string | null>(null);
  const [markingInstallmentId, setMarkingInstallmentId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("active");
  const [searchQuery, setSearchQuery] = useState("");
  const [locationFilter, setLocationFilter] = useState<string | undefined>(undefined);
  const [monthFilter, setMonthFilter] = useState<dayjs.Dayjs | undefined>(undefined);

  const loadInvestments = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const data = await fetchInvestments(clientId, token);
      setInvestments(data);
    } catch {
      message.error(t("Failed to load client investments"));
    } finally {
      setLoading(false);
    }
  }, [clientId, token, t]);

  const loadLocations = useCallback(async () => {
    if (!clientId) return;
    try {
      const data = await fetchLocations(clientId, token);
      setLocations(data);
    } catch {
      message.error(t("Failed to load locations"));
    }
  }, [clientId, token, t]);

  useEffect(() => {
    if (clientId && token) {
      loadInvestments();
      loadLocations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, token]);

  const upsertInvestment = (updated: ClientInvestment) => {
    setInvestments((prev) => {
      const exists = prev.some((inv) => inv._id === updated._id);
      if (exists) return prev.map((inv) => inv._id === updated._id ? { ...inv, ...updated } : inv);
      return [updated, ...prev];
    });
  };

  const removeInvestment = (id: string) => {
    setInvestments((prev) => prev.filter((inv) => inv._id !== id));
    if (drawerInvestment?._id === id) {
      setDrawerInvestment(null);
      setDetailsOpen(false);
    }
    if (selectedRowId === id) setSelectedRowId(null);
  };

  const syncInvestmentState = (updated: ClientInvestment) => {
    upsertInvestment(updated);
    if (drawerInvestment?._id === updated._id) setDrawerInvestment(updated);
  };

  const openInvestmentDrawer = (inv: ClientInvestment) => {
    setSelectedRowId(inv._id);
    setDrawerInvestment(inv);
    setDetailsOpen(true);
  };

  // paidAmount is the sum of payments, so lowering totalAmount below it leaves the
  // investment overpaid. Reduce the latest payment by the excess to keep them in sync.
  const trimOverpayment = async (inv: ClientInvestment): Promise<ClientInvestment> => {
    const excess = Number(inv.paidAmount || 0) - Number(inv.totalAmount || 0);
    if (excess <= 0) return inv;
    const full = inv.payments ? inv : await fetchInvestmentDetails(inv._id, token);
    const latest = [...(full.payments || [])].sort(
      (a, b) => new Date(b.paymentDate).getTime() - new Date(a.paymentDate).getTime(),
    )[0];
    if (!latest || latest.amount < excess) return full;
    const newAmount = Number((latest.amount - excess).toFixed(2));
    if (newAmount === 0) return deletePaymentApi(inv._id, latest._id, token);
    return updatePayment(inv._id, latest._id, { amount: newAmount }, token);
  };

  const handleSaveInvestment = async (values: any) => {
    if (!clientId) { message.error(t("Client is missing")); return; }
    setSavingInvestment(true);
    try {
      const payload = {
        ...values,
        clientId,
        startDate: values.startDate?.toISOString(),
        locationId: values.locationId || null,
      };
      let saved: ClientInvestment;
      if (editingInvestment) {
        saved = await updateInvestment(editingInvestment._id, payload, token);
        saved = await trimOverpayment(saved);
        message.success(t("Investment updated"));
      } else {
        saved = await createInvestment(payload, token);
        message.success(t("Investment created"));
      }
      syncInvestmentState(saved);
      setInvestmentFormOpen(false);
      setEditingInvestment(null);
    } catch {
      message.error(t("Failed to save client investment"));
      throw new Error("save failed");
    } finally {
      setSavingInvestment(false);
    }
  };

  const handleDeleteInvestment = async (investmentId: string) => {
    setDeletingId(investmentId);
    try {
      await deleteInvestment(investmentId, token);
      removeInvestment(investmentId);
      message.success(t("Investment deleted"));
    } catch {
      message.error(t("Failed to delete investment"));
    } finally {
      setDeletingId(null);
    }
  };

  const confirmDelete = (investmentId: string) => {
    Modal.confirm({
      title: t("Are you sure you want to delete this investment?"),
      okText: t("Delete"),
      cancelText: t("Cancel"),
      okButtonProps: { danger: true, loading: deletingId === investmentId },
      onOk: () => handleDeleteInvestment(investmentId),
    });
  };

  const openPaymentModal = (investmentId: string, payment?: ClientInvestmentPayment) => {
    paymentForm.resetFields();
    setPaymentModal({ open: true, investmentId, payment });
    paymentForm.setFieldsValue({
      amount: payment?.amount,
      paymentDate: payment ? dayjs(payment.paymentDate) : dayjs(),
      note: payment?.note,
    });
  };

  const handlePaymentSubmit = async () => {
    if (!paymentModal.investmentId) {
      message.warning(t("Please select an investment first"));
      return;
    }
    try {
      const values = await paymentForm.validateFields();
      setSavingPayment(true);
      let updated: ClientInvestment;
      if (paymentModal.payment) {
        updated = await updatePayment(
          paymentModal.investmentId,
          paymentModal.payment._id,
          {
            amount: values.amount,
            paymentDate: values.paymentDate?.toISOString(),
            note: values.note,
          },
          token,
        );
        message.success(t("Payment updated"));
      } else {
        updated = await addManualPayment(
          paymentModal.investmentId,
          Number(values.amount),
          token,
          values.note,
          values.paymentDate?.toISOString(),
        );
        message.success(t("Payment recorded"));
      }
      syncInvestmentState(updated);
      setPaymentModal({ open: false });
      paymentForm.resetFields();
    } catch {
      message.error(t("Failed to save payment"));
    } finally {
      setSavingPayment(false);
    }
  };

  const handleDeletePayment = async (investmentId: string, paymentId: string) => {
    setDeletingPaymentId(paymentId);
    try {
      const updated = await deletePaymentApi(investmentId, paymentId, token);
      syncInvestmentState(updated);
      message.success(t("Payment deleted"));
    } catch {
      message.error(t("Failed to delete payment"));
    } finally {
      setDeletingPaymentId(null);
    }
  };

  const confirmDeletePayment = (investmentId: string, payment: ClientInvestmentPayment) => {
    Modal.confirm({
      title: t("Are you sure you want to delete this payment?"),
      okText: t("Delete"),
      cancelText: t("Cancel"),
      okButtonProps: { danger: true, loading: deletingPaymentId === payment._id },
      onOk: () => handleDeletePayment(investmentId, payment._id),
    });
  };

  const handleMarkInstallment = async (investmentId: string) => {
    setMarkingInstallmentId(investmentId);
    try {
      const updated = await markInstallmentPaid(investmentId, token);
      syncInvestmentState(updated);
      message.success(t("Installment marked as paid"));
    } catch {
      message.error(t("Failed to save client investment"));
    } finally {
      setMarkingInstallmentId(null);
    }
  };

  const getLocationName = (locationId: ClientInvestment["locationId"]) => {
    if (typeof locationId === "object" && locationId !== null) return locationId.name;
    const match = locations.find((loc) => loc._id === locationId);
    return match?.name || "-";
  };

  const filteredInvestments = useMemo(() => {
    return investments.filter((inv) => {
      if (statusFilter !== "all" && inv.status.toLowerCase() !== statusFilter) return false;
      if (searchQuery && !inv.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
      if (locationFilter) {
        const invLocId =
          typeof inv.locationId === "object" && inv.locationId !== null
            ? inv.locationId._id
            : inv.locationId;
        if (invLocId !== locationFilter) return false;
      }
      if (monthFilter) {
        const mk = monthFilter.format("YYYY-MM");
        if (!inv.payments?.some((p) => dayjs(p.paymentDate).format("YYYY-MM") === mk)) return false;
      }
      return true;
    });
  }, [investments, statusFilter, searchQuery, locationFilter, monthFilter]);

  const totals = useMemo(
    () =>
      filteredInvestments.reduce(
        (acc, inv) => {
          acc.total += Number(inv.totalAmount || 0);
          acc.paid += Number(inv.paidAmount || 0);
          acc.remaining += Number(inv.remainingAmount || 0);
          return acc;
        },
        { total: 0, paid: 0, remaining: 0 },
      ),
    [filteredInvestments],
  );

  const activeCount = filteredInvestments.filter((i) => i.status === "ACTIVE").length;
  const collectPct = totals.total > 0 ? totals.paid / totals.total : 0;
  const C = 2 * Math.PI * 15;
  const donutOffset = C * (1 - collectPct);

  const hasFilters = !!(locationFilter || monthFilter);

  return (
    <div className="ci-page">
      {/* ===== Page header ===== */}
      <div className="ci-pg-head">
        <div>
          <h1>{t("Investments")}</h1>
          <div className="ci-pg-sub">
            {t("Simple investment tracking without affecting existing finance flows")}
          </div>
        </div>
        <button
          className="ci-new-btn"
          onClick={() => {
            setEditingInvestment(null);
            setInvestmentFormOpen(true);
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M12 5v14M5 12h14" />
          </svg>
          {t("New client investment")}
        </button>
      </div>

      {/* ===== KPI row ===== */}
      <div className="ci-kpi-row">
        {/* Total */}
        <div className="ci-kpi">
          <div className="ci-kpi-head">
            <span className="ci-kpi-label">{t("Total investment amount")}</span>
            <span className="ci-kpi-chip flat">{filteredInvestments.length} inv.</span>
          </div>
          <div className="ci-kpi-val">
            {fmt(totals.total)}
            <span className="ci-cur">RSD</span>
          </div>
          <div className="ci-kpi-bar">
            <i style={{ width: "100%" }} />
          </div>
          <div className="ci-kpi-foot">
            <span>{t("All investments")}</span>
            <span>{filteredInvestments.length} {t("total")}</span>
          </div>
        </div>

        {/* Paid */}
        <div className="ci-kpi">
          <div className="ci-kpi-head">
            <span className="ci-kpi-label">{t("Paid amount")}</span>
            <span className="ci-kpi-chip ok">▲ {Math.round(collectPct * 100)}%</span>
          </div>
          <div className="ci-kpi-val">
            {fmt(totals.paid)}
            <span className="ci-cur">RSD</span>
          </div>
          <div className="ci-kpi-bar">
            <i
              className="ok"
              style={{ width: `${Math.min(100, Math.round(collectPct * 100))}%` }}
            />
          </div>
          <div className="ci-kpi-foot">
            <span>{t("Collected so far")}</span>
            <span>{Math.round(collectPct * 100)}% {t("of total")}</span>
          </div>
        </div>

        {/* Remaining */}
        <div className="ci-kpi">
          <div className="ci-kpi-head">
            <span className="ci-kpi-label">{t("Remaining amount")}</span>
            <span className="ci-kpi-chip rose">{activeCount} {t("active")}</span>
          </div>
          <div className="ci-kpi-val">
            {fmt(totals.remaining)}
            <span className="ci-cur">RSD</span>
          </div>
          <div className="ci-kpi-bar">
            <i
              className="rose"
              style={{ width: `${totals.total > 0 ? Math.min(100, Math.round((totals.remaining / totals.total) * 100)) : 0}%` }}
            />
          </div>
          <div className="ci-kpi-foot">
            <span>{t("Outstanding balance")}</span>
            <span>{activeCount} {t("in progress")}</span>
          </div>
        </div>

        {/* Collection rate donut */}
        <div className="ci-kpi">
          <div className="ci-kpi-head">
            <span className="ci-kpi-label">{t("Collection rate")}</span>
            <span className="ci-kpi-chip ok">▲ {Math.round(collectPct * 100)}%</span>
          </div>
          <div className="ci-donut-wrap">
            <svg width="64" height="64" viewBox="0 0 40 40">
              <circle cx="20" cy="20" r="15" fill="none" stroke="var(--ci-line)" strokeWidth="5" />
              <circle
                cx="20"
                cy="20"
                r="15"
                fill="none"
                stroke="var(--ci-ok)"
                strokeWidth="5"
                strokeLinecap="round"
                strokeDasharray={`${C.toFixed(1)}`}
                strokeDashoffset={`${donutOffset.toFixed(1)}`}
                transform="rotate(-90 20 20)"
              />
              <text
                x="20"
                y="22"
                textAnchor="middle"
                fontSize="9"
                fontWeight="700"
                fontFamily="JetBrains Mono, monospace"
                fill="var(--ci-ink)"
              >
                {Math.round(collectPct * 100)}%
              </text>
            </svg>
            <div className="ci-donut-legend">
              <div className="ci-lg">
                <span className="ci-dot" style={{ background: "var(--ci-ok)" }} />
                {t("Paid")} <b>{fmtK(totals.paid)}</b>
              </div>
              <div className="ci-lg">
                <span className="ci-dot" style={{ background: "var(--ci-line)" }} />
                {t("Remaining")} <b>{fmtK(totals.remaining)}</b>
              </div>
            </div>
          </div>
          <div className="ci-kpi-foot">
            <span>{t("Active investments")}</span>
            <span>{activeCount} {t("in progress")}</span>
          </div>
        </div>
      </div>

      {/* ===== Toolbar ===== */}
      <div className="ci-toolbar">
        <div className="ci-search">
          <SearchIcon />
          <input
            placeholder={t("Search by name…")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="ci-seg">
          {(["all", "active", "completed"] as StatusFilter[]).map((s) => (
            <button
              key={s}
              className={statusFilter === s ? "active" : ""}
              onClick={() => setStatusFilter(s)}
            >
              {s === "all" ? t("All") : s === "active" ? t("Active") : t("Completed")}
            </button>
          ))}
        </div>

        <Select
          className="ci-filter-select"
          style={{ width: 180 }}
          placeholder={t("All locations")}
          allowClear
          value={locationFilter}
          onChange={(v) => setLocationFilter(v)}
          options={locations.map((loc) => ({ label: loc.name, value: loc._id }))}
        />

        <DatePicker
          className="ci-filter-datepicker"
          style={{ width: 160 }}
          picker="month"
          placeholder={t("Select month")}
          allowClear
          value={monthFilter}
          onChange={(v) => setMonthFilter(v || undefined)}
          format="MMM YYYY"
        />

        {hasFilters && (
          <button
            className="ci-clear-btn"
            onClick={() => { setLocationFilter(undefined); setMonthFilter(undefined); }}
          >
            {t("Clear filters")}
          </button>
        )}

        <span className="ci-tb-count">
          {t("Showing")} <b>{filteredInvestments.length}</b> {t("investments")}
        </span>
      </div>

      {/* ===== Table ===== */}
      <div className="ci-tbl-wrap">
        {loading ? (
          <div className="ci-tbl-loading">
            <Spin size="small" />
            <span>{t("Loading…")}</span>
          </div>
        ) : filteredInvestments.length === 0 ? (
          <div className="ci-tbl-empty">{t("No investments yet")}</div>
        ) : (
          <table className="ci-tbl">
            <colgroup>
              <col style={{ width: "22%" }} />
              <col style={{ width: "11%" }} />
              <col style={{ width: "11%" }} />
              <col style={{ width: "10%" }} />
              <col style={{ width: "13%" }} />
              <col style={{ width: "9%" }} />
              <col style={{ width: "10%" }} />
              <col style={{ width: "11%" }} />
              <col style={{ width: 38 }} />
            </colgroup>
            <thead>
              <tr>
                <th>{t("Investment name")}</th>
                <th className="num">{t("Total")}</th>
                <th className="num">{t("Paid")}</th>
                <th className="num">{t("Remaining")}</th>
                <th>{t("Progress")}</th>
                <th>{t("Status")}</th>
                <th>{t("Payment mode")}</th>
                <th>{t("Location")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filteredInvestments.map((inv) => {
                const done = inv.status === "COMPLETED";
                const pct = Math.round(inv.progress || 0);
                const locName = getLocationName(inv.locationId);
                const [locMain, locSub] = locName.includes(" - ")
                  ? locName.split(" - ")
                  : [locName, ""];
                const isManual = inv.paymentMode === "MANUAL";

                return (
                  <tr
                    key={inv._id}
                    className={selectedRowId === inv._id ? "sel" : ""}
                    onClick={() => openInvestmentDrawer(inv)}
                  >
                    <td>
                      <div className="ci-cell-name" title={inv.name}>{inv.name}</div>
                      <div className="ci-cell-date">
                        {inv.startDate ? dayjs(inv.startDate).format("YYYY-MM-DD") : "-"}
                      </div>
                    </td>

                    <td className="ci-num">{fmt(inv.totalAmount)}</td>

                    <td className={`ci-num${inv.paidAmount > 0 ? "" : " muted"}`}>
                      {fmt(inv.paidAmount)}
                    </td>

                    <td className={`ci-num${inv.remainingAmount > 0 ? " rose" : " muted"}`}>
                      {inv.remainingAmount > 0 ? fmt(inv.remainingAmount) : "0"}
                    </td>

                    <td>
                      <div className="ci-prog">
                        <div className="bar">
                          <i className={done ? "done" : ""} style={{ width: `${pct}%` }} />
                        </div>
                        <span className="pct">{pct}%</span>
                      </div>
                    </td>

                    <td>
                      <span className={`ci-chip ${done ? "completed" : "active"}`}>
                        <span className="d" />
                        {done ? t("COMPLETED") : t("ACTIVE")}
                      </span>
                    </td>

                    <td>
                      <span className={`ci-pay-mode ${isManual ? "manual" : "auto"}`}>
                        <span className="tag" />
                        <span>{isManual ? t("Manual") : t("Auto")}</span>
                      </span>
                    </td>

                    <td>
                      <div className="ci-loc-cell" title={locName}>
                        {locMain}
                        {locSub && <small>{locSub}</small>}
                      </div>
                    </td>

                    <td>
                      <Dropdown
                        trigger={["click"]}
                        menu={{
                          items: [
                            {
                              key: "edit",
                              label: t("Edit"),
                              icon: <EditOutlined />,
                              onClick: (info) => {
                                info.domEvent.stopPropagation();
                                setEditingInvestment(inv);
                                setInvestmentFormOpen(true);
                              },
                            },
                            {
                              key: "details",
                              label: t("Details"),
                              onClick: (info) => {
                                info.domEvent.stopPropagation();
                                openInvestmentDrawer(inv);
                              },
                            },
                            {
                              key: "payment",
                              label: t("Add manual payment"),
                              icon: <PlusOutlined />,
                              disabled: inv.remainingAmount <= 0,
                              onClick: (info) => {
                                info.domEvent.stopPropagation();
                                openPaymentModal(inv._id);
                              },
                            },
                            {
                              key: "mark",
                              label: t("Mark installment as paid"),
                              disabled: inv.remainingAmount <= 0,
                              onClick: (info) => {
                                info.domEvent.stopPropagation();
                                handleMarkInstallment(inv._id);
                              },
                            },
                            { type: "divider" as const },
                            {
                              key: "delete",
                              label: t("Delete"),
                              icon: <DeleteOutlined />,
                              danger: true,
                              onClick: (info) => {
                                info.domEvent.stopPropagation();
                                confirmDelete(inv._id);
                              },
                            },
                          ],
                        }}
                      >
                        <button
                          className="ci-kebab-btn"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <KebabIcon />
                        </button>
                      </Dropdown>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ===== Investment form modal ===== */}
      <InvestmentFormModal
        open={investmentFormOpen}
        loading={savingInvestment}
        onSubmit={handleSaveInvestment}
        onClose={() => {
          setInvestmentFormOpen(false);
          setEditingInvestment(null);
        }}
        investment={editingInvestment}
        locations={locations}
      />

      {/* ===== Details drawer ===== */}
      <InvestmentDetailsDrawer
        investment={drawerInvestment}
        open={detailsOpen}
        onClose={() => {
          setDetailsOpen(false);
          setSelectedRowId(null);
        }}
        onAddManualPayment={(id) => openPaymentModal(id)}
        onMarkInstallment={handleMarkInstallment}
        markingInstallmentId={markingInstallmentId}
        onEditInvestment={(investment) => {
          setEditingInvestment(investment);
          setInvestmentFormOpen(true);
        }}
        onEditPayment={(investmentId, payment) => openPaymentModal(investmentId, payment)}
        onDeletePayment={(investmentId, payment) =>
          confirmDeletePayment(investmentId, payment)
        }
        deletingPaymentId={deletingPaymentId}
      />

      {/* ===== Payment modal ===== */}
      <Modal
        open={paymentModal.open}
        title={paymentModal.payment ? t("Edit payment") : t("Add manual payment")}
        className="standardHeader standardFooter ci-form-modal"
        okText={paymentModal.payment ? t("Update") : t("Save")}
        cancelText={t("Cancel")}
        onCancel={() => {
          setPaymentModal({ open: false });
          paymentForm.resetFields();
        }}
        onOk={handlePaymentSubmit}
        confirmLoading={savingPayment}
      >
        <Form layout="vertical" form={paymentForm}>
          <Form.Item
            name="amount"
            label={t("Enter payment amount")}
            rules={[{ required: true, message: t("Please enter amount") }]}
          >
            <InputNumber style={{ width: "100%" }} min={1} />
          </Form.Item>
          <Form.Item
            name="paymentDate"
            label={t("Payment date")}
            rules={[{ required: true, message: t("Please select date") }]}
          >
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="note" label={t("Note")}>
            <Input.TextArea rows={3} placeholder={t("Optional note")} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ClientInvestments;
